import { css } from '@emotion/react';
import classNames from 'classnames';
import React, { ReactElement } from 'react';
import { button_base_css, button_size_css, getButtonStyle, button_size_icon_size_css } from './styles';
import type { BaseButtonProps, ButtonColor, ButtonSize, ButtonVariant } from './types';
import { ButtonIcon } from './ButtonIcon';
import { IconProps } from '../icons/generated';
import { IconLoadingDonuts } from '../icons/generated';
import { Slot, Slottable } from '@radix-ui/react-slot';

export type { ButtonColor, ButtonVariant } from './types';

export interface ButtonProps extends BaseButtonProps {
  fill?: boolean;
  /** @default 'filled' */
  variant?: ButtonVariant;
  /** @default 'secondary' */
  color?: ButtonColor;
  /** @default 'medium' */
  size?: ButtonSize;
  /** @default false */
  loading?: boolean;
  /**
   * 버튼 좌측 영역에 렌더링 될 아이콘 요소
   *  - Icon 컴포넌트 color props가 없으면 variant와 color에 따라 currentColor가 지정됩니다.
   *  - Icon 컴포넌트 size props가 있더라도 Icon 사이즈는 ButtonSize에 따라 고정됩니다.
   */
  leftIcon?: ReactElement<IconProps>;
  /**
   * 버튼 우측 영역에 렌더링 될 아이콘 요소
   *  - Icon 컴포넌트 color props가 없으면 variant와 color에 따라 currentColor가 지정됩니다.
   *  - Icon 컴포넌트 size props가 있더라도 Icon 사이즈는 ButtonSize에 따라 고정됩니다.
   */
  rightIcon?: ReactElement<IconProps>;
}

export interface ButtonIconProps {
  size: ButtonSize;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      type = 'button',
      size = 'medium',
      variant = 'filled',
      color = 'secondary',
      fill,
      disabled,
      className,
      loading = false,
      leftIcon,
      rightIcon,
      asChild = false,
      ...props
    },
    ref,
  ) => {
    const isDisabled = disabled || loading;
    const icon_props = {
      size: button_size_icon_size_css[size],
      color: 'currentColor',
    };
    const loading_indicator = loading && (
      <ButtonIcon {...icon_props}>
        <IconLoadingDonuts size={30} />
      </ButtonIcon>
    );
    const left_icon_node = leftIcon && (
      <ButtonIcon {...icon_props}>{loading ? <IconLoadingDonuts size={30} /> : leftIcon}</ButtonIcon>
    );
    const right_icon_node = rightIcon && (
      <ButtonIcon {...icon_props}>{loading && !leftIcon ? <IconLoadingDonuts size={30} /> : rightIcon}</ButtonIcon>
    );
    const Comp = asChild ? Slot : 'button';

    return (
      <Comp
        type={type}
        className={classNames('pds-button', className)}
        css={[
          button_base_css,
          getButtonStyle(variant, color),
          button_size_css[size],
          css`
            ${fill && 'width: 100%;'}
            cursor: ${isDisabled ? 'not-allowed' : 'pointer'};
          `,
        ]}
        disabled={isDisabled}
        {...props}
        ref={ref}
      >
        {left_icon_node || (!right_icon_node && loading_indicator)}
        <Slottable>{children}</Slottable>
        {right_icon_node}
      </Comp>
    );
  },
);
